import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchWithTimeout } from "../../hooks/useFetchWithTimeout";
import { GetStorageLink } from "../../BackendAccess/FrontendHelpers";
import PageWrapper from "../pageWrapper";
//CSS
import "./warehousePage.css";
import "../0PageStyles.css";

//Components
import WarehouseCard from "../../Components/Warehouse Components/warehouseCard";
import WarehouseCreateCard from "../../Components/Warehouse Components/warehouseCreateCard";
import SearchBar from "../../Components/Other Components/searchBar";
import Title from "../../Components/Other Components/Title";
import SortComponent from "../../Components/Other Components/sortComponent";
import Button from "../../Components/Other Components/button";
import PageLoader from "../../Components/Other Components/pageLoader";
import ExportData from "../../Components/Other Components/ExportDataComponent";

//Functions and hooks
import { GetWarehouses } from "../../BackendAccess/WarehouseHooks";
import { useErrorHandler } from "../../hooks/errorHandler";
import { useNotification } from "../../Contexts/NotificationContext";
import { useTranslation } from "react-i18next";

export default function WarehousePage() {
  const navigate = useNavigate();
  const location = useLocation();

  //States
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(null);
  const [pageError, setPageError] = useState(null);
  //States end

  const { showNotification } = useNotification();
  useErrorHandler(error, showNotification, setPageError);

  const { t } = useTranslation();

  // Fetch items from the server
  const fetchItems = async () => {
    setLoading(true);
    try {
      const [data, error] = await fetchWithTimeout(() => GetWarehouses());

      if (error !== null) {
        error.isPageError = true;
        setError(error);
      } else {
        var itemsSorted = data.sort((a, b) => (a.Name > b.Name ? 1 : -1));
        setItems(itemsSorted);
      }
    } catch (e) {
      setError({ errCode: 500, err: t("Server error") });
      return;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [location.pathname]);

  // Handle card clicks
  const handleCardClick = async (id) => {
    navigate("/workspace/warehouses/warehouseEditPage", { state: { id } });
  };

  // Handle creation card click
  const clickOnCreationCard = () => {
    navigate("/workspace/warehouses/warehouseCreation");
  };

  // Handle search input
  const handleSearchChange = (term) => {
    setSearchTerm(term);
  };

  // Filter items based on search term
  const filteredItems = items.filter((item) => {
    const lowerCaseTerm = searchTerm.toLowerCase();
    return (
      item.Name.toLowerCase().includes(lowerCaseTerm) ||
      item.WarehouseID.toString().toLowerCase().includes(lowerCaseTerm) ||
      item.Address.toLowerCase().includes(lowerCaseTerm)
    );
  });

  //Sort function by name
  const nameSortFunction = (direction) => {
    var itemsSorted = [...items].sort((a, b) =>
      a.Name.toUpperCase() > b.Name.toUpperCase()
        ? direction * 1
        : -1 * direction
    );
    setItems(itemsSorted);
  };

  //Sort function by id
  const idSortFunction = (direction) => {
    var itemsSorted = [...items].sort((a, b) =>
      a.WarehouseID > b.WarehouseID ? direction * 1 : -1 * direction
    );
    setItems(itemsSorted);
  };

  const tutorialSteps = [
    {
      target: null,
      title: t("Your warehouses", { ns: "tutorial" }),
      message: t(
        "This is your Warehouses page - every physical location where you store stock lives here. Each warehouse has its own inventory, separate from the others.",
        { ns: "tutorial" }
      ),
    },
    {
      target: ".SearchBar",
      title: t("Search", { ns: "tutorial" }),
      message: t(
        "Search for a warehouse by name, ID, or address.",
        { ns: "tutorial" }
      ),
    },
    {
      target: ".sort-component",
      title: t("Sort", { ns: "tutorial" }),
      message: t(
        "Sort the warehouses by name or ID, ascending or descending.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "warehouses-create-btn",
      title: t("Create", { ns: "tutorial" }),
      message: t(
        "Add a new warehouse here, or use the '+' card at the end of the list below - they both do the same thing.",
        { ns: "tutorial" }
      ),
    },
    {
      target: ".warehouse-card:first-of-type",
      title: t("Warehouse cards", { ns: "tutorial" }),
      message: t(
        "Each card is one warehouse, showing its photo, name, ID, and address. Click a card to open it, see what's stocked inside, and edit its details.",
        { ns: "tutorial" }
      ),
    },
    {
      target: ".export-container",
      title: t("Export"),
      message: t(
        "Export the current warehouse list to Excel or CSV.",
        { ns: "tutorial" }
      ),
    },
  ];

  return (
    <PageWrapper tutorialId="warehouses" tutorialSteps={tutorialSteps}>
      <PageLoader loading={loading} error={pageError}>
        <div className="page-background"></div>
        <div className="page-content">
          <Title className="mt-3">{t("Warehouses")}</Title>
          <div className="container-empty vertical w-90">
            <SearchBar
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              placeholder={t("Search warehouses...", { ns: "warehouse" })}
            />
          </div>
          <div className="spacer s"></div>
          <div className="container-empty w-90">
            <SortComponent
              sortOpts={["Name", "ID"]}
              sortOptFunctions={[nameSortFunction, idSortFunction]}
            />
            <div className="container-empty horizontal-reverse">
              <Button
                onClick={clickOnCreationCard}
                btnStyle={"btn-positive"}
                btnSize={"btn-small"}
                icon={"create"}
                tutorialId="warehouses-create-btn"
              >
                {t("Create")}
              </Button>
            </div>
          </div>
          <div className="spacer s"></div>
          <div className="container-empty w-90">
            <div className="grid-container">
              {loading ? (
                <p>{t("Loading")}...</p>
              ) : (
                filteredItems.map((item) => (
                  <WarehouseCard
                    key={item.WarehouseID}
                    Name={item.Name}
                    ID={item.WarehouseID}
                    Address={item.Address}
                    imgSrc={GetStorageLink() + item.ImgPath}
                    onClick={() => handleCardClick(item.WarehouseID)}
                    disabled={loading}
                  />
                ))
              )}
              <WarehouseCreateCard onClick={clickOnCreationCard} />
            </div>
          </div>
          {<ExportData data={filteredItems} />}
        </div>
      </PageLoader>
    </PageWrapper>
  );
}
